<template>
  <div class="flex gap-2">
    <!-- Day Field -->
    <div class="flex-1" :class="dayOrderClass">
      <USelect
        v-model="day"
        :options="dayOptions"
        placeholder="Day"
        :disabled="disabled"
        size="lg"
      />
    </div>
    
    <!-- Month Field -->
    <div class="flex-1" :class="monthOrderClass">
      <USelect
        v-model="month"
        :options="monthOptions"
        placeholder="Month"
        :disabled="disabled"
        size="lg"
      />
    </div>
    
    <!-- Year Field -->
    <div class="flex-1" :class="yearOrderClass">
      <USelect
        v-model="year"
        :options="yearOptions"
        placeholder="Year"
        :disabled="disabled"
        size="lg"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
defineOptions({
  name: 'BDOBInput'
});

const model = defineModel<string>({
  required: true,
  default: ''
});

const props = defineProps<{
  disabled?: boolean;
}>();

const { $i18n } = useNuxtApp();

const day = ref();
const month = ref();
const year = ref();

const dayOrderClass = computed(() => {
  const locale = String($i18n.locale?.value || 'en');
  
  if (locale.startsWith('en-US')) {
    return 'order-2'; // Day second in US format (M/D/Y)
  } else if (locale.startsWith('ja') || locale.startsWith('hu') || locale.startsWith('fa')) {
    return 'order-3'; // Day last in Y/M/D format
  } else {
    return 'order-1'; // Day first in D/M/Y format
  }
});

const monthOrderClass = computed(() => {
  const locale = String($i18n.locale?.value || 'en');
  
  if (locale.startsWith('en-US')) {
    return 'order-1'; // Month first in US format (M/D/Y)
  } else if (locale.startsWith('ja') || locale.startsWith('hu') || locale.startsWith('fa')) {
    return 'order-2'; // Month second in Y/M/D format
  } else {
    return 'order-2'; // Month second in D/M/Y format
  }
});

const yearOrderClass = computed(() => {
  const locale = String($i18n.locale?.value || 'en');
  
  if (locale.startsWith('en-US')) {
    return 'order-3'; // Year last in US format (M/D/Y)
  } else if (locale.startsWith('ja') || locale.startsWith('hu') || locale.startsWith('fa')) {
    return 'order-1'; // Year first in Y/M/D format
  } else {
    return 'order-3'; // Year last in D/M/Y format
  }
});


const getDaysInMonth = (month: number, year?: number) => {
  if (!year) {
    // Conservative approach when year is not set
    const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return daysInMonth[month - 1] || 31;
  }
  return new Date(year, month, 0).getDate();
};

const dayOptions = computed(() => {
  let maxDays = 31;
  if (month.value) {
    const yearNum = year.value ? parseInt(year.value) : undefined;
    maxDays = getDaysInMonth(parseInt(month.value), yearNum);
  }
  return Array.from({ length: maxDays }, (_, i) => ({
    value: String(i + 1).padStart(2, '0'),
    label: String(i + 1)
  }));
});

const monthOptions = computed(() => {
  return Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1).padStart(2, '0'),
    label: new Date(2000, i).toLocaleDateString($i18n.locale || 'en', { month: 'long' })
  }));
});

const yearOptions = computed(() => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 120 }, (_, i) => {
    const yearValue = currentYear - i;
    return {
      value: String(yearValue),
      label: String(yearValue)
    };
  });
});

// Watch for changes in month to validate day
watch(month, (newMonth) => {
  if (day.value && newMonth) {
    const yearNum = year.value ? parseInt(year.value) : undefined;
    const maxDays = getDaysInMonth(parseInt(newMonth), yearNum);
    if (parseInt(day.value) > maxDays) {
      day.value = '';
    }
  }
});

// Watch for changes in year to validate day (leap year check)
watch(year, (newYear) => {
  if (day.value && month.value && newYear) {
    const maxDays = getDaysInMonth(parseInt(month.value), parseInt(newYear));
    if (parseInt(day.value) > maxDays) {
      day.value = '';
    }
  }
});

// Watch for any field changes to update model value
watch([day, month, year], () => {
  updateModelValue();
});

const updateModelValue = () => {
  if (day.value && month.value && year.value) {
    const dateValue = `${year.value}${month.value.padStart(2, '0')}${day.value.padStart(2, '0')}`;
    model.value = dateValue;
  } else {
    model.value = '';
  }
};

const parseModelValue = (value: string | undefined) => {
  if (!value) {
    day.value = '';
    month.value = '';
    year.value = '';
    return;
  }

  const _value = value.toString();

  if (_value.length === 8) {
    // YYYYMMDD format
    year.value = _value.substring(0, 4);
    month.value = _value.substring(4, 6);
    day.value = _value.substring(6, 8);
  } else {
    day.value = '';
    month.value = '';
    year.value = '';
  }
};

watch(() => model.value, (newValue) => {
  if (newValue !== undefined) {
    parseModelValue(newValue);
  }
}, { immediate: true });

</script>